// Use dynamic import to avoid Node.js constructor issues during module load
// Socket type for TypeScript
import type { Socket } from 'socket.io-client';

/**
 * Socket.IO service singleton GLOBALNY dla całej platformy
 * Zarządza jednym połączeniem WebSocket współdzielonym przez wszystkie moduły
 * Zapewnia:
 * - Wykrywanie luk w sekwencji notyfikacji
 * - Automatyczny resync przy lukach ≤ 50
 * - Full reload przy lukach > 50
 * - Resync po reconnect
 */

type NotificationCallback = (evt: any) => void | Promise<void>;
type LoadDataCallback = () => Promise<void>;

class SocketService {
  private socket: Socket | null = null;
  private lastSeq: number = 0;
  private listeners: Set<NotificationCallback> = new Set();
  private loadDataCallback: LoadDataCallback | null = null;
  private isProcessing: boolean = false;
  private readonly GAP_THRESHOLD = 50;
  private readonly STORAGE_KEY = 'platform.socket.lastSeq';
  private ioModule: any = null; // Cache dla dynamicznego importu

  /**
   * Inicjalizuje Socket.IO połączenie (singleton)
   * Używa lazy loading - jeśli socket już istnieje, zwraca go synchronicznie
   * W przeciwnym razie ładuje moduł asynchronicznie
   */
  init(): Socket | Promise<Socket> {
    if (this.socket) {
      console.log('[SocketService] Already initialized, returning existing instance');
      return this.socket;
    }

    // Jeśli moduł jest już załadowany, utwórz socket synchronicznie
    if (this.ioModule) {
      return this._createSocket();
    }

    // W przeciwnym razie załaduj moduł asynchronicznie
    return this._initAsync();
  }

  /**
   * Asynchroniczna inicjalizacja z dynamicznym importem
   */
  private async _initAsync(): Promise<Socket> {
    console.log('[SocketService] Initializing new Socket.IO instance (async)');

    // Sprawdź czy jesteśmy w przeglądarce
    if (typeof window === 'undefined') {
      const error = new Error('[SocketService] Cannot initialize socket.io-client outside browser environment');
      console.error(error);
      throw error;
    }

    // Dynamiczny import socket.io-client aby uniknąć problemów z konstruktorami Node.js
    try {
      console.log('[SocketService] Loading socket.io-client dynamically...');
      const socketIoModule = await import('socket.io-client');
      this.ioModule = socketIoModule;
      console.log('[SocketService] socket.io-client loaded successfully');
    } catch (error: any) {
      console.error('[SocketService] Failed to load socket.io-client:', error);
      throw new Error(`[SocketService] Failed to load socket.io-client: ${error?.message || 'Unknown error'}`);
    }

    return this._createSocket();
  }

  /**
   * Tworzy instancję socketu (wymaga załadowanego modułu)
   */
  private _createSocket(): Socket {
    const io = this.ioModule.io || this.ioModule.default?.io || this.ioModule.default;
    if (!io || typeof io !== 'function') {
      const error = new Error('[SocketService] socket.io-client "io" function is not available. Call init() first.');
      console.error(error);
      throw error;
    }

    // Przywróć lastSeq z localStorage
    const stored = localStorage.getItem(this.STORAGE_KEY);
    this.lastSeq = Number(stored || 0);
    console.log('[SocketService] Restored lastSeq from localStorage:', this.lastSeq);

    try {
      // Utwórz połączenie z dodatkowymi opcjami dla przeglądarki
      this.socket = io(undefined, { 
        withCredentials: true,
        // Force browser transport (prevent Node.js code paths)
        transports: ['websocket', 'polling'],
        // Disable Node.js-specific features
        forceNew: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity
      });
      console.log('[SocketService] Socket instance created successfully');
    } catch (error: any) {
      console.error('[SocketService] Error creating socket:', error);
      console.error('[SocketService] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      throw new Error(`[SocketService] Failed to initialize socket.io-client: ${error?.message || 'Unknown error'}`);
    }

    // Zapisz w window dla kompatybilności wstecznej
    (window as any).__socket_io_global__ = this.socket;

    // Attach handlers
    this.socket.on('connect', () => this.handleConnect());
    this.socket.on('disconnect', (reason) => this.handleDisconnect(reason));
    this.socket.on('connect_error', (err) => console.warn('[SocketService] connect_error:', err?.message || err));
    this.socket.on('error', (err) => console.warn('[SocketService] error:', err?.message || err));
    this.socket.on('reconnect_attempt', (n) => console.log('[SocketService] reconnect_attempt:', n));
    this.socket.on('reconnect_error', (err) => console.warn('[SocketService] reconnect_error:', err?.message || err));
    this.socket.on('db:change', (evt) => this.handleNotification(evt));

    return this.socket;
  }

  /**
   * Rejestruje callback do przetwarzania notyfikacji
   * @returns funkcja do wyrejestrowania
   */
  subscribe(callback: NotificationCallback): () => void {
    this.listeners.add(callback);
    console.log('[SocketService] Subscriber added, total:', this.listeners.size);
    
    return () => {
      this.listeners.delete(callback);
      console.log('[SocketService] Subscriber removed, total:', this.listeners.size);
    };
  }

  /**
   * Rejestruje callback do pełnego przeładowania danych
   */
  setLoadDataCallback(callback: LoadDataCallback) {
    this.loadDataCallback = callback;
  }

  /**
   * Zwraca aktualny lastSeq
   */
  getLastSeq(): number {
    return this.lastSeq;
  }

  /**
   * Aktualizuje lastSeq i zapisuje w localStorage
   */
  setLastSeq(seq: number) {
    this.lastSeq = seq;
    try {
      localStorage.setItem(this.STORAGE_KEY, String(seq));
    } catch (err) {
      console.warn('[SocketService] Failed to save lastSeq to localStorage:', err);
    }
  }

  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return false;
      return Date.now() >= (exp * 1000) - 30000; // 30 second buffer
    } catch {
      return true;
    }
  }

  /**
   * Clear auth cookie and redirect to login
   */
  private handleAuthFailure(): void {
    console.warn('[SocketService] Auth failure detected, closing socket and redirecting to login');
    
    // Close socket to stop reconnection attempts
    this.close();
    
    // Clear tokens
    try {
      sessionStorage.removeItem('authToken');
    } catch {}
    
    // Clear cookie on all domains
    try {
      const host = window.location.hostname;
      const parts = host.split('.');
      const domains = [host];
      if (parts.length > 2) domains.push(parts.slice(1).join('.'));
      if (parts.length > 3) domains.push(parts.slice(2).join('.'));
      for (const domain of domains) {
        document.cookie = `authToken=; domain=.${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
      }
    } catch {}
    
    // Redirect to portal
    const parts = window.location.hostname.split('.');
    if (parts.length >= 3) {
      const tenantHost = parts.slice(1).join('.');
      window.location.assign(`https://${tenantHost}/`);
    } else {
      window.location.assign('/');
    }
  }

  /**
   * Helper to add Authorization header to fetch requests
   */
  private async authenticatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Get token from sessionStorage (same as axios interceptor)
    let token: string | null = null;
    try {
      if (typeof window !== 'undefined') {
        token = sessionStorage.getItem('authToken');
      }
    } catch (e) {
      // sessionStorage not available (e.g., private browsing)
    }

    // Check if token is expired BEFORE making request
    if (token && this.isTokenExpired(token)) {
      console.warn('[SocketService] Token expired, handling auth failure');
      this.handleAuthFailure();
      throw new Error('Token expired');
    }

    // Merge headers
    const headers = new Headers(init?.headers);
    
    // Add Authorization header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Merge with existing init options
    const newInit: RequestInit = {
      ...init,
      headers,
      credentials: init?.credentials || 'include', // Keep credentials: 'include' for CORS
    };

    const response = await fetch(input, newInit);
    
    // Handle 401/403 from backend
    if (response.status === 401 || response.status === 403) {
      console.warn('[SocketService] Got 401/403 from backend, handling auth failure');
      this.handleAuthFailure();
      throw new Error('Unauthorized');
    }

    return response;
  }

  /**
   * Obsługa connect/reconnect
   */
  private async handleConnect() {
    console.log('[SocketService] Connected/reconnected, socket.id:', this.socket?.id);

    // Sprawdź, czy podczas disconnect przyszły notyfikacje
    if (this.lastSeq > 0) {
      try {
        console.log('[SocketService] Checking for missed notifications since seq:', this.lastSeq);
        const resp = await this.authenticatedFetch(`/api/notifications/since/${this.lastSeq}?limit=100`, { 
          credentials: 'include' 
        });

        if (resp.ok) {
          const data = await resp.json();

          if (data.notifications.length > this.GAP_THRESHOLD) {
            console.log(`[SocketService] Many missed notifications (${data.notifications.length} > ${this.GAP_THRESHOLD}), performing full reload`);
            await this.loadDataCallback?.();
          } else if (data.notifications.length > 0) {
            console.log(`[SocketService] Resyncing ${data.notifications.length} missed notifications`);
            await this.resyncNotifications(data.notifications);
            console.log('[SocketService] Reconnect resync completed');
          } else {
            console.log('[SocketService] No missed notifications');
          }
        }
      } catch (err) {
        console.error('[SocketService] Reconnect resync failed:', err);
      }
    }
  }

  /**
   * Obsługa disconnect
   */
  private handleDisconnect(reason: any) {
    console.log('[SocketService] Disconnected, reason:', reason);
  }

  /**
   * Centralna obsługa notyfikacji z gap detection
   */
  private async handleNotification(evt: any) {
    if (!evt) return;
    if (this.isProcessing) {
      console.warn('[SocketService] Already processing, queuing might be needed');
    }

    this.isProcessing = true;

    try {
      const currentSeq = Number(evt.seq);

      // Sprawdź gap tylko jeśli mamy sequence numbers
      if (currentSeq > 0 && this.lastSeq > 0) {
        const gap = currentSeq - this.lastSeq - 1;

        if (gap > 0) {
          console.warn('[SocketService] Gap detected:', {
            lastSeq: this.lastSeq,
            currentSeq,
            gap,
            threshold: this.GAP_THRESHOLD
          });

          // Duża luka → full reload
          if (gap > this.GAP_THRESHOLD) {
            console.log(`[SocketService] Gap ${gap} > ${this.GAP_THRESHOLD}, performing full reload`);
            await this.loadDataCallback?.();
            this.isProcessing = false;
            return; // loadDataCallback aktualizuje lastSeq
          }

          // Mała luka → resync
          try {
            console.log(`[SocketService] Resyncing ${gap} missing notifications`);
            const resp = await this.authenticatedFetch(`/api/notifications/since/${this.lastSeq}?limit=100`, {
              credentials: 'include'
            });

            if (!resp.ok) {
              console.error('[SocketService] Resync failed, performing full reload');
              await this.loadDataCallback?.();
              this.isProcessing = false;
              return;
            }

            const data = await resp.json();

            // Sprawdź, czy log nie został wyczyszczony
            if (data.oldest_seq > this.lastSeq) {
              console.warn('[SocketService] Notification log was cleaned, performing full reload');
              await this.loadDataCallback?.();
              this.isProcessing = false;
              return;
            }

            // Przetwórz brakujące notyfikacje
            await this.resyncNotifications(data.notifications, currentSeq);
          } catch (err) {
            console.error('[SocketService] Resync error, performing full reload:', err);
            await this.loadDataCallback?.();
            this.isProcessing = false;
            return;
          }
        }
      }

      // Przetwórz bieżącą notyfikację
      await this.broadcastToListeners(evt);

      // Aktualizuj lastSeq
      if (currentSeq > 0) {
        this.setLastSeq(currentSeq);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Resync brakujących notyfikacji
   */
  private async resyncNotifications(notifications: any[], maxSeq?: number) {
    for (const n of notifications) {
      // Nie przetwarzaj notyfikacji nowszych niż current event
      if (maxSeq && n.id > maxSeq) break;

      await this.broadcastToListeners(n.payload);
      this.setLastSeq(n.id);
    }
    console.log('[SocketService] Resync completed, processed', notifications.length, 'notifications');
  }

  /**
   * Broadcast notyfikacji do wszystkich subskrybentów
   */
  private async broadcastToListeners(evt: any) {
    for (const listener of this.listeners) {
      try {
        await listener(evt);
      } catch (err) {
        console.error('[SocketService] Listener error:', err);
      }
    }
  }

  /**
   * Zamknij połączenie (cleanup)
   */
  close() {
    if (this.socket) {
      console.log('[SocketService] Closing socket connection');
      this.socket.close();
      this.socket = null;
      this.listeners.clear();
      delete (window as any).__socket_io_global__;
    }
  }

  /**
   * Pobierz instancję socketu (jeśli potrzebna bezpośrednia interakcja)
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Singleton instance
export const socketService = new SocketService();

